import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { IpcRendererEvent, IpcRenderOn, IpcRenderRemove, IpcRenderSend } from "./ipcRenderer";
import { actions } from "../store";
import { useIsSoftphoneEnabled } from '../app-hooks';
import { selectEndpoint, selectLine } from 'webrtc-client-janus';
import { useSelector } from '../hook-utils';
import { createPromiseSource, delay, PromiseSource } from '../promise-utils';

const ChannelName = 'telephone-controller'

interface TelephoneControllerEvent {
  Online: boolean
}

type DialCommand = {
  Command: 'dial'
  Number: string
}

export type TelephoneCommand = DialCommand

export function updateTelephoneControllerStatus(online: boolean) {
  IpcRenderSend<TelephoneControllerEvent>(`${ChannelName}-event`, {
    Online: online,
  })
}

export function useElectronTelephoneController() {
  const dispatch = useDispatch()
  const endpoint = useSelector(selectEndpoint)
  const line = useSelector(selectLine)
  const isSoftphoneEnabled = useIsSoftphoneEnabled()
  const isSoftphoneOnline = isSoftphoneEnabled && endpoint.status === 'online' && line.status === 'registered'
  const cancel = useRef<PromiseSource<false> | null>(null)
  const phoneNumber = useRef<string>('')

  const telephoneControllerHandle = useCallback((event: IpcRendererEvent, arg: TelephoneCommand) => {
    const dial = async () => {
      if (!isSoftphoneEnabled || isSoftphoneOnline) {
        dispatch(actions.ccDial(phoneNumber.current))
      } else {
        const retryDelay = delay(15000)
        cancel.current = createPromiseSource<false>()
        await Promise.race([retryDelay, cancel.current.promise])
        // at this point we don't care what happens and we're just going to try and dial.
        console.log('performing delayed dial to ', phoneNumber.current)
        dispatch(actions.ccDial(phoneNumber.current))
      }
    }

    console.log('Received telephone command from electron', arg)
    switch (arg.Command) {
      case 'dial':
        phoneNumber.current = arg.Number
        dial()
        break
    }
  }, [dispatch, isSoftphoneEnabled, isSoftphoneOnline])

  useEffect(() => {
    if (cancel.current) {
      if (isSoftphoneOnline) {
        // softphone is null only we can cancel the await.
        console.log('Softphone is now online, will attempt to dial')
        cancel.current.resolve(false)
        cancel.current = null
        return
      }
    }
  }, [isSoftphoneOnline])

  useEffect(() => {
    IpcRenderOn('telephone-controller', telephoneControllerHandle)

    return () => {
      IpcRenderRemove('telephone-controller', telephoneControllerHandle)
    }
  }, [telephoneControllerHandle])
}
